import { Image } from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService, { BaseURL } from "../../../service/Api";
import { tokens } from "../../../theme";
import AssignModal from "./AssignModal";
import {
  AssignableUser,
  ApiResponse,
  SiteVisitAssignee,
  SiteVisitListResponse,
  UsersResponse,
  Venue,
} from "./venues-widget.types";
import UserAvatarRow from "./UserAvatarRow";

export default function VenuesWidget() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [draggingUser, setDraggingUser] = useState<AssignableUser | null>(null);
  const assigningUserRef = useRef<AssignableUser | null>(null);
  const [dragOverVenueId, setDragOverVenueId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [assignedUsersByVenueId, setAssignedUsersByVenueId] = useState<
    Record<number, SiteVisitAssignee>
  >({});

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await apiService.get<ApiResponse>(
          "/api/venues/venue/",
          { page: 1, page_size: 1000 },
        );

        const sorted = (response.data?.results || []).sort(
          (a, b) => (a.completeness_score ?? 0) - (b.completeness_score ?? 0),
        );

        setVenues(sorted.slice(0, 10));
      } catch (error) {
        console.error("Failed to fetch venues", error);
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchAssignedSiteVisits = async () => {
      try {
        const response = await apiService.get<SiteVisitListResponse>(
          "/api/venues/site-visit/",
          { page: 1, page_size: 1000, status: "assigned" },
        );

        const assignedMap: Record<number, SiteVisitAssignee> = {};

        response.data.results.forEach((visit) => {
          const assignedUser = visit.assignees?.[0];
          if (visit.status === "assigned" && visit.venue?.id && assignedUser) {
            assignedMap[visit.venue.id] = assignedUser;
          }
        });

        setAssignedUsersByVenueId(assignedMap);
      } catch (error) {
        console.error("Failed to fetch assigned site visits", error);
      }
    };

    fetchAssignedSiteVisits();
  }, []);

  useEffect(() => {
    const fetchAssignableUsers = async () => {
      try {
        setUsersLoading(true);

        const response = await apiService.get<UsersResponse>(
          "/api/authentication/users/",
          { role: ["admin", "dev", "sales"], page: 1, page_size: 1000 },
        );

        setAssignableUsers(response.data.results || []);
      } catch (error) {
        console.error("Failed to fetch assignable users", error);
        setAssignableUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchAssignableUsers();
  }, []);

  /* ── helpers ── */
  const getVenueImageUrl = (venue?: Venue) => {
    if (!venue?.files?.length) return null;
    const image =
      venue.files.find((f) => f.file_type === "venue_photos") || venue.files[0];
    return image?.file || null;
  };

  const getMediaUrl = (path?: string | null) => {
    if (!path) return undefined;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const cleanBaseUrl = BaseURL.replace(/\/$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBaseUrl}${cleanPath}`;
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const width = scrollContainerRef.current.clientWidth;
      const newIndex = Math.round(scrollLeft / width);
      if (newIndex !== activeIndex) setActiveIndex(newIndex);
    }
  };

  const scrollTo = (index: number) => {
    if (scrollContainerRef.current) {
      const width = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: width * index,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  /* ── drag handlers ── */
  const handleDragStart = (user: AssignableUser) => {
    setDraggingUser(user);
  };

  const handleDragOver = (e: React.DragEvent, venueId: number) => {
    e.preventDefault();
    setDragOverVenueId(venueId);
  };

  const handleDragLeave = () => {
    setDragOverVenueId(null);
  };

  const handleDrop = (e: React.DragEvent, venue: Venue) => {
    e.preventDefault();
    setDragOverVenueId(null);
    if (!draggingUser) return;
    assigningUserRef.current = draggingUser;
    setSelectedVenue(venue);
    setModalOpen(true);
  };

  const handleDragEnd = () => {
    setDraggingUser(null);
    setDragOverVenueId(null);
  };

  /* ── confirm assignment ── */
  const handleConfirmAssign = async (date: string) => {
    const user = assigningUserRef.current;
    if (!selectedVenue || !user) return;

    const res = await apiService.get<SiteVisitListResponse>(
      "/api/venues/site-visit/",
      { venue: selectedVenue.id },
    );

    const siteVisit = res.data.results?.[0];
    if (!siteVisit) throw new Error("No site visit found for this venue");

    await apiService.patch(`/api/venues/site-visit/${siteVisit.id}/`, {
      scheduled_date_time: new Date(date).toISOString(),
      assignee_ids: [user.id],
      status: "assigned",
    });

    setAssignedUsersByVenueId((prev) => ({
      ...prev,
      [selectedVenue.id]: user,
    }));
    setModalOpen(false);
    setSelectedVenue(null);
    assigningUserRef.current = null;
  };

  const slide1 = venues.slice(0, 4);
  const slide2 = venues.slice(4, 8);
  const slide3 = venues.slice(8, 10);
  const featuredVenue = slide1[0];
  const featuredVenueImage = getVenueImageUrl(featuredVenue);

  const dots = [];
  if (slide1.length > 0) dots.push(0);
  if (slide2.length > 0) dots.push(1);
  if (slide3.length > 0) dots.push(2);

  const renderListItem = (venue: Venue, rank: number) => {
    const percent = Math.round(venue.completeness_score ?? 0);
    const isOver = dragOverVenueId === venue.id;
    const assignedUser = assignedUsersByVenueId[venue.id];
    const isAssigned = Boolean(assignedUser);

    return (
      <Box
        key={venue.id}
        draggable={false}
        onDragOver={(e) => handleDragOver(e, venue.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, venue)}
        onClick={() => navigate(`/venues/view/${venue.id}`)}
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${isOver ? colors.primary[400] : colors.primary[800]}`,
          borderRadius: "16px",
          p: 1,
          cursor: draggingUser ? "copy" : "pointer",
          transition: "all 0.2s ease",
          backgroundColor: isOver ? `${colors.primary[400]}22` : "transparent",
          transform: isOver ? "scale(1.01)" : "scale(1)",
          "&:hover": {
            backgroundColor: draggingUser
              ? `${colors.primary[400]}22`
              : colors.primary[800],
            transform: draggingUser ? "scale(1.01)" : "translateY(-2px)",
          },
        }}
      >
        <Typography
          fontWeight={800}
          color={colors.primary[400]}
          sx={{ width: 40, textAlign: "center", fontSize: "2.2rem" }}
        >
          {rank}
        </Typography>

        <Box sx={{ flexGrow: 1, ml: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color={colors.primary[700]}
            noWrap
          >
            {venue.name}
          </Typography>
          <Typography
            variant="caption"
            color={colors.primary[300]}
            noWrap
            display="block"
          >
            {venue.address_one || "No address"}
          </Typography>
        </Box>

        {isAssigned && assignedUser ? (
          <Tooltip
            title={
              `${assignedUser.first_name} ${assignedUser.last_name}`.trim() ||
              assignedUser.email
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Avatar
                src={getMediaUrl(assignedUser.profile_picture_url)}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: "#22c55e",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                {`${assignedUser.first_name?.[0] ?? ""}${assignedUser.last_name?.[0] ?? ""}`.toUpperCase() ||
                  "?"}
              </Avatar>
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={100}
              sx={{ color: colors.primary[800] }}
              size={40}
              thickness={4}
            />
            <CircularProgress
              variant="determinate"
              value={percent}
              sx={{ color: "#F57C00", position: "absolute", left: 0 }}
              size={40}
              thickness={4}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                component="div"
                color={colors.primary[700]}
                sx={{ fontSize: "0.6rem", fontWeight: "bold" }}
              >
                {percent}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderFeaturedCard = (venue: Venue) => {
    const isOver = dragOverVenueId === venue.id;
    const assignedUser = assignedUsersByVenueId[venue.id];
    const isAssigned = Boolean(assignedUser);

    return (
      <Box
        onDragOver={(e) => handleDragOver(e, venue.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, venue)}
        onClick={() => navigate(`/venues/view/${venue.id}`)}
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          height: 120,
          mb: 2,
          cursor: draggingUser ? "copy" : "pointer",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          outline: isOver ? `3px solid ${colors.primary[400]}` : "none",
          transform: isOver ? "scale(1.02)" : "scale(1)",
          "&:hover": { transform: "scale(1.02)" },
        }}
      >
        {featuredVenueImage ? (
          <Box
            component="img"
            src={featuredVenueImage}
            alt={venue.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[400]} 100%)`,
              color: "white",
            }}
          >
            <Image sx={{ fontSize: 40, opacity: 0.7 }} />
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, rgba(0,0,0,0.75), rgba(0,0,0,0.25))",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="caption" color="white">
              {venue.venue_type || "Venue"}
            </Typography>

            {isAssigned && assignedUser ? (
              <Tooltip
                title={
                  `${assignedUser.first_name} ${assignedUser.last_name}`.trim() ||
                  assignedUser.email
                }
              >
                <Avatar
                  src={getMediaUrl(assignedUser.profile_picture_url)}
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: "#22c55e",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    border: "2px solid white",
                  }}
                >
                  {`${assignedUser.first_name?.[0] ?? ""}${assignedUser.last_name?.[0] ?? ""}`.toUpperCase() ||
                    "?"}
                </Avatar>
              </Tooltip>
            ) : (
              <Box
                sx={{
                  bgcolor: "#F57C00",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="white">
                  {Math.round(venue.completeness_score ?? 0)}%
                </Typography>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight="bold" color="white">
              {venue.name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
              {venue.address_one || "No address"}
            </Typography>
          </Box>
        </Box>

        {isOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${colors.primary[400]}44`,
              backdropFilter: "blur(2px)",
            }}
          >
            <Typography fontWeight={700} color="white" fontSize="0.9rem">
              Drop to assign
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  /* ── render ── */
  return (
    <>
      <Paper
        elevation={0}
        onDragEnd={handleDragEnd}
        sx={{
          borderRadius: "24px",
          p: 2,
          height: "100%",
          backgroundColor: colors.primary[900],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={800}
          color={colors.primary[700]}
          sx={{ display: "block", mb: 1 }}
        >
          VENUES NEEDING ATTENTION
        </Typography>

        <UserAvatarRow
          users={assignableUsers}
          loading={usersLoading}
          onDragStart={handleDragStart}
        />

        {loading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={26} sx={{ color: colors.primary[400] }} />
          </Box>
        ) : venues.length === 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color={colors.primary[300]}>
              No venues found
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                display: "flex",
                flexGrow: 1,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {/* Slide 1 */}
              <Box
                sx={{
                  minWidth: "100%",
                  scrollSnapAlign: "start",
                  display: "flex",
                  flexDirection: "column",
                  pr: slide2.length > 0 ? 2 : 0,
                }}
              >
                {featuredVenue && renderFeaturedCard(featuredVenue)}
                <Stack spacing={2}>
                  {slide1
                    .slice(1)
                    .map((venue, index) => renderListItem(venue, index + 2))}
                </Stack>
              </Box>

              {/* Slide 2 */}
              {slide2.length > 0 && (
                <Box
                  sx={{
                    minWidth: "100%",
                    scrollSnapAlign: "start",
                    display: "flex",
                    flexDirection: "column",
                    pr: slide3.length > 0 ? 2 : 0,
                  }}
                >
                  <Stack spacing={2}>
                    {slide2.map((venue, index) =>
                      renderListItem(venue, index + 5),
                    )}
                  </Stack>
                </Box>
              )}

              {/* Slide 3 */}
              {slide3.length > 0 && (
                <Box
                  sx={{
                    minWidth: "100%",
                    scrollSnapAlign: "start",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack spacing={2}>
                    {slide3.map((venue, index) =>
                      renderListItem(venue, index + 9),
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            {dots.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                  gap: 0.5,
                }}
              >
                {dots.map((index) => (
                  <Box
                    key={index}
                    onClick={() => scrollTo(index)}
                    sx={{
                      width: activeIndex === index ? 16 : 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor:
                        activeIndex === index
                          ? colors.primary[400]
                          : colors.primary[50],
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Paper>

      <AssignModal
        open={modalOpen}
        venue={selectedVenue}
        user={assigningUserRef.current}
        onClose={() => {
          setModalOpen(false);
          setSelectedVenue(null);
          assigningUserRef.current = null;
        }}
        onConfirm={handleConfirmAssign}
      />
    </>
  );
}
